/**
 * Pilot personal-gear loadout limits + gate — the single source the reducer wall
 * (`add_pilot_gear`) and the editor-kit affordance both import, so the per-category
 * caps can never drift between the gate and the control. The same discipline as
 * `licenses.ts` / `budgets.ts` / `sp.ts`.
 *
 * The model: a pilot's on-foot gear is a flat
 * id list (`PilotData.gear`), **no duplicates** (distinct picks). The category a
 * given item counts against — **weapon / armor / gear** — is **derived off the
 * compendium item's `type` field at gate time, never stored** (the same "raw state
 * minimal, derive against the compendium" discipline as the license/manufacturer
 * seams). Per RAW (Core p. 34) pilot gear is free of any LL/license gating; the
 * only gate is the per-category quantity limit.
 *
 * **Limits are canonical, read off `@massif/lancer-data`'s `rules` (`rules.json`)**
 * via the compendium package's `pilotLoadoutLimits()` — `max_pilot_weapons` (2),
 * `max_pilot_armor` (1), `max_pilot_gear` (3). They are NOT hardcoded here so the
 * numbers can't drift from the rulebook.
 *
 * **No bonus flow here.** 5/37 pilot-gear items (the armor) carry `bonuses` that
 * target pilot on-foot stats (`pilot_hp`/`pilot_armor`/`pilot_evasion`/`pilot_edef`/
 * `pilot_speed`). This module stays gate-only — the compute that resolves those
 * bonuses lives in `@lancer-vtt/rules`' `computePilotStats`/`collectPilotGearBonuses`
 * (`packages/rules/src/compute.ts`, landing as `ComputedPilot.combat`), which consumes
 * this gear model as its source. Do NOT add a pilot-stat compute here.
 */
import { pilotLoadoutLimits } from "@lancer-vtt/compendium";
import type { PilotData } from "./types";

/** A pilot-gear category, derived off the compendium item's `type` — never stored. */
export type PilotGearCategory = "weapon" | "armor" | "gear";

/**
 * The RAW per-category pilot-loadout limits, read off `rules.json` (Core p. 34):
 * `{ weapon: 2, armor: 1, gear: 3 }`. The single source the reducer wall and the kit
 * affordance share. Module-level so the read happens once.
 */
export const PILOT_GEAR_LIMITS: Readonly<Record<PilotGearCategory, number>> =
  pilotLoadoutLimits();

/**
 * The minimal compendium shape the pilot-gear gate needs: resolve a `pilot_gear` id
 * to (at least) its `type`. A structural slice (mirrors `sp.ts`'s `SpCompendium` /
 * `licenses.ts`'s `LicenseCompendium`) so the domain package leans on the compendium's
 * *types* only, not a concrete class.
 */
export type PilotGearCompendium = {
  getById(type: "pilot_gear", id: string): { type?: string } | undefined;
};

/**
 * Bucket a compendium pilot-gear item into its category off its `type` field. The
 * data uses capitalized `"Weapon"`/`"Armor"`/`"Gear"`; anything else (absent/unknown)
 * falls through to `"gear"`, the catch-all bucket.
 */
export function pilotGearCategory(item: { type?: string }): PilotGearCategory {
  switch ((item.type ?? "").toLowerCase()) {
    case "weapon":
      return "weapon";
    case "armor":
      return "armor";
    default:
      return "gear";
  }
}

/**
 * Add or remove a pilot-gear id in a flat id list (immutable), no duplicates.
 * `present: true` appends the id (no-op if already present); `present: false` removes
 * it. Returns a new array — the swap that backs both `add_pilot_gear`/`remove_pilot_gear`
 * and their symmetric inverses. Mirrors `withCoreBonus`'s shape for the id-list model.
 */
export function withPilotGear(gear: string[], id: string, present: boolean): string[] {
  if (!present) return gear.filter((g) => g !== id);
  if (gear.includes(id)) return gear;
  return [...gear, id];
}

/**
 * Count a pilot's taken gear per category, bucketed by each item's resolved `type`.
 * An id that doesn't resolve against the compendium is skipped (it counts toward no
 * category) — mirrors the core-bonus prereq's derive-then-skip discipline.
 */
export function pilotGearCounts(
  pilot: PilotData,
  compendium: PilotGearCompendium,
): Record<PilotGearCategory, number> {
  const counts: Record<PilotGearCategory, number> = { weapon: 0, armor: 0, gear: 0 };
  for (const id of pilot.gear) {
    const item = compendium.getById("pilot_gear", id);
    if (!item) continue;
    counts[pilotGearCategory(item)] += 1;
  }
  return counts;
}

/**
 * Whether the pilot may add `item` without exceeding its category's limit. The
 * predicate the reducer wall (`add_pilot_gear`) and the kit affordance share, so the
 * cap is single-sourced. Args-object form (the codebase's `max-params: 3` convention,
 * mirroring `meetsCoreBonusPrereq`). Note this does NOT short-circuit an
 * already-present id — the reducer skips the gate on a no-op re-add.
 */
export function canAddPilotGear(args: {
  pilot: PilotData;
  item: { type?: string };
  compendium: PilotGearCompendium;
}): boolean {
  const { pilot, item, compendium } = args;
  const category = pilotGearCategory(item);
  const counts = pilotGearCounts(pilot, compendium);
  return counts[category] < PILOT_GEAR_LIMITS[category];
}
